import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, MapPin, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';

// --- IMPORTS ---
import AddPatientModal from '../components/AddPatientModal';
import ViewPatientModal from '../components/ViewPatientModal';
import EditPatientModal from '../components/EditPatientModal';
import ZoomableImage from '../components/ZoomableImage';
import DutyTypeSelect from '../components/DutyTypeSelect';
import SearchableSelect from '../components/SearchableSelect';
import ConfirmDialog from '../components/ConfirmDialog';
import { useReferenceStore, ReferenceOption, ReferenceStore } from '../store/useReferenceStore';

// Inline-editable cells: plain text until hovered or focused, so the table still
// reads as a table rather than a wall of form controls. Mirrors the management
// staff table's `.icell`, which does the same job there.
const inlineCellCss = `
  .pdtbl .icell { width: 100%; border: 1px solid transparent; background: transparent; border-radius: 4px;
                  font: inherit; color: inherit; text-align: center; padding: 3px 4px; outline: none; cursor: pointer; }
  .pdtbl .icell:hover { border-color: #cbd5e1; background: #f8fafc; }
  .pdtbl .icell:focus { border-color: #2563eb; background: #fff; cursor: text; box-shadow: 0 0 0 2px rgba(37,99,235,0.12); }
  /* The placeholder is the empty-value dash, so it should read as an absent
     value rather than as hint text shouting for attention. */
  .pdtbl .icell::placeholder { color: #94a3b8; opacity: 1; }
  .pdtbl .icell.saving { opacity: 0.5; pointer-events: none; }
`;

const PLACEHOLDER_IMG = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

// --- CONSTANTS ---
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000'; 

const DUTY_TYPES = ['08 Hrs', '12 Dual', '12 Day', '12 Night', '24 Hrs', 'Nursing Visit'];
const STATUS_OPTIONS = ['Active', 'Suspended', 'Stopped', 'Discharged'];
const MANAGERS = ['Leo Devasia', 'Deepak Manuel', 'Jibi Mathew'];

// --- UPDATED SORT ORDER ---
const DUTY_SORT_ORDER = [
    '08 Hrs',
    '12 Dual',
    '12 Day',
    '12 Night',
    '24 Hrs',
    'Nursing Visit',
    'Physiotherapy',
    'Medical Equipment',
    'Consumables',
];

// --- Interfaces ---
interface ClientData {
  client_name?: string; 
  id?: number;
  name?: string;        
  relationship: string;
  contact_number_1?: string;
  contact_number?: string;
  contact_number_2?: string;
  secondary_contact_number?: string;
  email: string;
}

interface Patient {
  id: number;
  case_start_date: string;
  duty_type: string;
  duty_type_specify?: string;
  // duty_schedule removed from logic
  
  patient_clients?: ClientData[];
  clients_info?: ClientData[]; 
  
  address: string;
  location_link?: string;
  patient_name: string;
  age: string | number;
  sex: string;
  case_manager: string;
  status: string;
  photo_url?: string;
  
  treating_details?: any[];
}

const PatientDashboard: React.FC = () => {
  // Auto-open the New Client Registration form when arrived via /patients?new=1
  const [isModalOpen, setIsModalOpen] = useState(() => new URLSearchParams(window.location.search).get('new') === '1');
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(null);
  
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoverRowId, setHoverRowId] = useState<number | null>(null);
  const [openStatusId, setOpenStatusId] = useState<number | null>(null);

  // --- Inline editing of Duty Type / Case Manager, straight from the table ---
  // `pendingEdit` holds the pick until the confirm dialog is answered; nothing
  // is written before then. `savingCell` dims the one cell being written.
  interface PendingEdit {
    patient: Patient;
    field: 'duty_type' | 'case_manager';
    value: string | null;
    /** Column name, for the dialog and the toast. */
    title: string;
    /** What it reads now, for the dialog's before → after line. */
    from: string;
  }
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);
  const [savingCell, setSavingCell] = useState<string | null>(null);
  // SearchableSelect holds the text typed into it in its own state, and only
  // resyncs when the selected value changes. Since a pick here isn't written
  // until the dialog is answered, an abandoned pick would otherwise be left
  // showing in the cell. Bumping this remounts the cells, resetting them to
  // what the record actually says.
  const [cellResetKey, setCellResetKey] = useState(0);
  // The same reference list the registration form's Case Manager dropdown uses,
  // so a name added in either place shows up in the other.
  const refOptions = useReferenceStore((s: ReferenceStore) => s.options);
  const addReferenceOption = useReferenceStore((s: ReferenceStore) => s.addOption);

  // --- MULTI-SELECT FILTER STATE ---
  const [dutyFilters, setDutyFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [managerFilters, setManagerFilters] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');


  // --- HELPER COMPONENTS ---
  const CopyButton = ({ text }: { text: string | undefined }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e: React.MouseEvent) => {
        e.preventDefault(); 
        e.stopPropagation();
        if (text) {
            navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000); 
        }
    };

    return (
        <button
            onClick={handleCopy}
            title={copied ? "Copied!" : "Copy to clipboard"}
            style={{
                background: 'transparent', 
                border: 'none', 
                cursor: 'pointer',
                display: 'inline-flex', 
                alignItems: 'center', 
                marginLeft: '6px',
                padding: '2px', 
                borderRadius: '4px'
            }}
        >
            {copied ? <Check size={13} color="#16a34a" /> : <Copy size={13} color="#475569" />}
        </button>
    );
  };

  const CopyableText = ({ text, children }: { text: string | undefined, children: React.ReactNode }) => {
      const [copied, setCopied] = useState(false);

      const handleCopy = (e: React.MouseEvent) => {
          e.preventDefault(); 
          e.stopPropagation();
          if (text) {
              navigator.clipboard.writeText(text);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000); 
          }
      };

      return (
          <div 
            onClick={handleCopy}
            title="Click to copy"
            style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer', 
                width: 'fit-content' 
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'} 
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
              {children}
              <div style={{ marginLeft: '6px', display: 'flex', alignItems: 'center' }}>
                  {copied ? <Check size={13} color="#16a34a" /> : <Copy size={13} color="#475569" />}
              </div>
          </div>
      );
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return '-';
    const parts = dateString.split('-'); 
    if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}-${month}-${year}`;
    }
    return dateString;
  };

  // --- UPDATED DISPLAY HELPER ---
  // This now strictly returns Duty Type (or the specified value if "Specify" was chosen)
  const getDisplayDuty = (patient: Patient) => {
      if (patient.duty_type === 'Specify' && patient.duty_type_specify) {
          return patient.duty_type_specify;
      }
      return patient.duty_type || '-';
  };

  // Every case-manager name on offer in the cell editor: the shared reference
  // list first, then the screen's built-in names and anything already sitting on
  // a record — a manager who was never added to the list still has to be
  // selectable, and a record holding an off-list name must still display it.
  const caseManagerOptions = useMemo(() => {
    const fromList = (refOptions['case_manager'] || []).map((o: ReferenceOption) => o.label);
    const onRecords = patients.map(p => p.case_manager).filter(Boolean) as string[];
    return Array.from(new Set([...fromList, ...MANAGERS, ...onRecords]));
  }, [refOptions, patients]);

  // Duty types on offer: the standard set, plus whatever this case currently
  // holds if it's something else (a "Specify" value, or an older record), so
  // opening the menu never silently drops the value already there.
  const dutyOptionsFor = (patient: Patient) => {
    const current = getDisplayDuty(patient);
    return current && current !== '-' && !DUTY_TYPES.includes(current)
      ? [...DUTY_TYPES, current]
      : DUTY_TYPES;
  };

  const fetchPatients = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setError("Authentication token not found.");
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/api/patients/?limit=200`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const getSortedClients = (p: Patient) => {
          const rawClients = p.patient_clients && p.patient_clients.length > 0
              ? p.patient_clients
              : (p.clients_info || []);
          return [...rawClients].sort((a, b) => (a.id || 0) - (b.id || 0));
      };
      
      const sortedData = response.data.sort((a: Patient, b: Patient) => {
          // 1. Get Duty Types
          const typeA = a.duty_type || '';
          const typeB = b.duty_type || '';

          // 2. Find Index in the fixed Sort Order
          let indexA = DUTY_SORT_ORDER.indexOf(typeA);
          let indexB = DUTY_SORT_ORDER.indexOf(typeB);

          if (indexA === -1) indexA = 999;
          if (indexB === -1) indexB = 999;

          // 3. Primary Sort: By Duty Order
          if (indexA !== indexB) {
              return indexA - indexB;
          }

          // 4. Secondary Sort: Alphabetical by FIRST CLIENT Name
          const clientsA = getSortedClients(a);
          const clientsB = getSortedClients(b);
          
          const nameA = (clientsA[0]?.client_name || clientsA[0]?.name || '').trim().toLowerCase();
          const nameB = (clientsB[0]?.client_name || clientsB[0]?.name || '').trim().toLowerCase();

          return nameA.localeCompare(nameB);
      });

      setPatients(sortedData);
    } catch (err) {
      setError("Failed to fetch patient data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (openStatusId === null) return;
    const close = () => setOpenStatusId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openStatusId]);


  // --- FILTER HELPERS ---
  const addFilter = (currentList: string[], setter: React.Dispatch<React.SetStateAction<string[]>>, newValue: string) => {
    if (newValue && !currentList.includes(newValue)) {
      setter([...currentList, newValue]);
    }
  };

  const removeFilter = (currentList: string[], setter: React.Dispatch<React.SetStateAction<string[]>>, valueToRemove: string) => {
    setter(currentList.filter(item => item !== valueToRemove));
  };

  const resetAllFilters = () => {
    setDutyFilters([]);
    setStatusFilters([]);
    setManagerFilters([]);
    setSearchQuery('');
  };

  // --- FILTER LOGIC ---
  const filteredPatients = useMemo(() => {
    return patients.filter(patient => {
       const lowerQuery = searchQuery.toLowerCase();
       
       const clients = patient.patient_clients && patient.patient_clients.length > 0 
            ? patient.patient_clients 
            : (patient.clients_info || []);

       const matchClientInfo = clients.some(c => {
           const cName = (c.client_name || c.name || '').toLowerCase();
           const cPhone1 = (c.contact_number_1 || c.contact_number || '').toLowerCase();
           const cPhone2 = (c.contact_number_2 || c.secondary_contact_number || '').toLowerCase();
           return cName.includes(lowerQuery) || cPhone1.includes(lowerQuery) || cPhone2.includes(lowerQuery);
       });

       const matchDirectFields = 
           searchQuery === '' ||
           String(patient.id).includes(lowerQuery) ||
           patient.patient_name.toLowerCase().includes(lowerQuery) ||
           (patient.address || '').toLowerCase().includes(lowerQuery) ||
           (patient.duty_type || '').toLowerCase().includes(lowerQuery);

       const matchSearch = matchDirectFields || matchClientInfo;
       
       let matchDuty = true;
       if (dutyFilters.length > 0) {
           matchDuty = dutyFilters.includes(patient.duty_type) || (dutyFilters.includes('Others') && !DUTY_TYPES.includes(patient.duty_type));
       }

       let matchStatus = true;
       if (statusFilters.length > 0) {
           matchStatus = statusFilters.includes(patient.status);
       }

       let matchManager = true;
       if (managerFilters.length > 0) {
           matchManager = managerFilters.includes(patient.case_manager);
       }

       return matchSearch && matchDuty && matchStatus && matchManager;
    });
  }, [patients, dutyFilters, statusFilters, managerFilters, searchQuery]);

  const totalPatients = patients.length;
  const activeCases = patients.filter(p => p.status?.toLowerCase() === 'active').length;
  const dischargedCount = patients.filter(p => p.status?.toLowerCase() === 'stopped' || p.status?.toLowerCase() === 'discharged').length;

  // --- Handlers ---
  const handleViewDetails = (id: number) => {
    setSelectedPatientId(id);
    setIsDetailModalOpen(true);
  };

  const handleEditDetails = (id: number) => {
    setSelectedPatientId(id);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (id: number) => {
     if(window.confirm("Are you sure you want to delete this patient? This action cannot be undone.")) {
         const token = localStorage.getItem('accessToken');
         try {
            await axios.delete(`${API_BASE_URL}/api/patients/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchPatients();
         } catch (error) {
             alert("Failed to delete patient");
         }
     }
  }

  // One field, changed in place on an otherwise untouched record.
  //
  // The PATCH takes the whole patient, so the current record is read back first
  // and the one field overlaid — sending only the changed key would blank
  // everything else. clients_info is remapped because the API returns clients
  // under different names than it accepts them under.
  const savePatientField = async (patient: Patient, patch: Record<string, any>) => {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/patients/${patient.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const current = res.data;
    const formData = new FormData();
    formData.append('patient_data', JSON.stringify({
      ...current,
      ...patch,
      clients_info: (current.patient_clients || []).map((c: any) => ({
        id: c.id,
        name: c.client_name,
        relationship: c.relationship,
        contact_number: c.contact_number_1,
        secondary_contact_number: c.contact_number_2,
        email: c.email,
      })),
    }));
    await axios.patch(`${API_BASE_URL}/api/patients/${patient.id}`, formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
    });
    setPatients(prev => prev.map(p => p.id === patient.id ? { ...p, ...patch } : p));
  };

  const handleStatusChange = async (patient: Patient, newStatus: string) => {
    try {
      await savePatientField(patient, { status: newStatus });
    } catch (err) {
      alert('Failed to update status');
    }
  };

  // Duty type and case manager are edited straight from the table, but both are
  // case-level facts that show up on bills, the operations board and the case
  // sheet — so the change is confirmed before it is written, rather than
  // happening on a stray click in a dense table.
  const commitPendingEdit = async () => {
    const edit = pendingEdit;
    setPendingEdit(null);
    if (!edit) return;
    setSavingCell(`${edit.patient.id}:${edit.field}`);
    try {
      await savePatientField(edit.patient, { [edit.field]: edit.value });
      toast.success(`${edit.title} updated`);
    } catch (err) {
      toast.error(`Failed to update ${edit.title.toLowerCase()}`);
    } finally {
      setSavingCell(null);
      setCellResetKey(k => k + 1);
    }
  };

  const cancelPendingEdit = () => {
    setPendingEdit(null);
    setCellResetKey(k => k + 1);
  };

  const handleSuccess = () => {
    setIsModalOpen(false);
    fetchPatients();
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    fetchPatients();
  };

  const getImageUrl = (path: string | undefined) =>
  path ? (path.startsWith('http') ? path : `${API_BASE_URL}/${path}`) : PLACEHOLDER_IMG;

  const getStatusStyle = (status: string) => {
    const map: Record<string, { bg: string; color: string; border: string; dot: string }> = {
      Active:    { bg: '#f0fdf4', color: '#15803d', border: '#86efac', dot: '#22c55e' },
      Stopped:   { bg: '#fff7ed', color: '#c2410c', border: '#fdba74', dot: '#f97316' },
      Suspended: { bg: '#fefce8', color: '#854d0e', border: '#fde047', dot: '#eab308' },
      Discharged:{ bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5', dot: '#ef4444' },
    };
    return map[status] || { bg: '#f8fafc', color: '#475569', border: '#cbd5e1', dot: '#94a3b8' };
  };

  // --- STYLES ---
  const styles: { [key: string]: React.CSSProperties } = {
    page: { padding: '1.25rem', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: '"Inter", sans-serif' },

    // Header
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' },
    titleGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
    title: { margin: 0, fontSize: '1.6rem', fontWeight: 700, color: '#0f172a' },
    subtitle: { margin: 0, color: '#64748b', fontSize: '0.85rem' },
    avatar: {
        width: '72px',
        height: '72px',
        borderRadius: '8px',
        objectFit: 'cover',
        border: '1px solid #e2e8f0'
    },
    headerControls: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
    // Buttons & Inputs
    addButton: {
        backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '8px',
        padding: '0.5rem 1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: '0.4rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)', transition: 'all 0.2s', whiteSpace: 'nowrap'
    },
    inlineSelect: {
        padding: '0.5rem 0.6rem', borderRadius: '8px', border: '1px solid #cbd5e1',
        fontSize: '0.82rem', color: '#334155', outline: 'none', cursor: 'pointer',
        backgroundColor: '#fff', height: '34px',
    },
    searchInput: {
        padding: '0.5rem 0.75rem 0.5rem 2rem', borderRadius: '8px', border: '1px solid #cbd5e1',
        fontSize: '0.85rem', color: '#334155', width: '220px', outline: 'none', backgroundColor: '#fff',
    },
    clearBtn: {
        border: 'none', background: '#fef2f2', color: '#ef4444',
        fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
        padding: '0.5rem 0.9rem', borderRadius: '8px', whiteSpace: 'nowrap' as const,
    },
    chip: {
        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
        backgroundColor: '#eff6ff', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: 600,
        padding: '0.2rem 0.55rem', borderRadius: '999px', border: '1px solid #bfdbfe'
    },
    chipRemove: { cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#bfdbfe', color: '#1e40af', fontSize: '10px' },

    // Table — exactly matches BillingIndex
    tableCard: {
        backgroundColor: 'white', borderRadius: '12px',
        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.025)',
        border: '1px solid #e2e8f0', overflow: 'hidden',
    },
    tableWrapper: { width: '100%', overflowX: 'auto' },
    table: { width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', color: '#1d2631' },
    thead: { backgroundColor: '#86afd8', borderBottom: '1px solid #e2e8f0' },
    th: {
        padding: '0.55rem 0.3rem', color: '#475569', fontWeight: 700, fontSize: '0.68rem',
        textTransform: 'uppercase', letterSpacing: '0.04em',
        border: '1px solid #c5cbd3', textAlign: 'center', lineHeight: 1.3,
    },
    tbody: { backgroundColor: 'white' },
    tr: { borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.1s' },
    td: { padding: '0.45rem 0.35rem', verticalAlign: 'middle', border: '1px solid #c5cbd3', textAlign: 'center', fontSize: '0.76rem', wordBreak: 'break-word', lineHeight: 1.35 },
    textBtn: {
        padding: '0.3rem 0.5rem', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 600,
        cursor: 'pointer', border: 'none', transition: 'all 0.2s', display: 'inline-block',
        whiteSpace: 'nowrap' as const,
    },
    viewBtn: {
        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white',
        boxShadow: '0 4px 6px -1px rgba(79,70,229,0.2)',
    },
    editBtn: {
        background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)', color: 'white',
        boxShadow: '0 4px 6px -1px rgba(37,99,235,0.2)',
    },
    deleteBtn: {
        background: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)', color: 'white',
        boxShadow: '0 4px 6px -1px rgba(225,29,72,0.2)',
    },

    // Helpers
    boldText: { fontWeight: 600, color: '#0f172a' },
    clientBlock: { marginBottom: '2px', color: '#334155', fontWeight: 500, fontSize: '0.76rem' },
    contactBlock: { fontSize: '0.72rem', color: '#64748b' }
  };


  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.titleGroup}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <h1 style={styles.title}>Client List</h1>
            {!loading && (
              <>
                <span style={{ padding: '0.2rem 0.65rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }}>
                  {totalPatients} Total
                </span>
                <span style={{ padding: '0.2rem 0.65rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>
                  {activeCases} Active
                </span>
                {dischargedCount > 0 && (
                  <span style={{ padding: '0.2rem 0.65rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
                    {dischargedCount} Stopped
                  </span>
                )}
              </>
            )}
          </div>
          <p style={styles.subtitle}>Manage patient cases, duty types, and client details.</p>
        </div>
        <div style={styles.headerControls}>
          <button style={styles.addButton} onClick={() => setIsModalOpen(true)}>
            <Plus size={16} /> <span>Add Client</span>
          </button>
          <select
            style={styles.inlineSelect}
            value=""
            onChange={(e) => addFilter(dutyFilters, setDutyFilters, e.target.value)}
          >
            <option value="" disabled>Duty Type</option>
            {DUTY_TYPES.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
          <select
            style={styles.inlineSelect}
            value=""
            onChange={(e) => addFilter(statusFilters, setStatusFilters, e.target.value)}
          >
            <option value="" disabled>Status</option>
            {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
          <select
            style={styles.inlineSelect}
            value=""
            onChange={(e) => addFilter(managerFilters, setManagerFilters, e.target.value)}
          >
            <option value="" disabled>Case Manager</option>
            {MANAGERS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <svg width="14" height="14" fill="none" stroke="#94a3b8" viewBox="0 0 24 24"
              style={{ position: 'absolute', left: '9px', pointerEvents: 'none' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search name, mobile..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          {(dutyFilters.length > 0 || statusFilters.length > 0 || managerFilters.length > 0 || searchQuery) && (
            <button style={styles.clearBtn} onClick={resetAllFilters}>Clear</button>
          )}
        </div>
      </header>

      {/* Active filter chips */}
      {(dutyFilters.length > 0 || statusFilters.length > 0 || managerFilters.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
          {dutyFilters.map(item => (
            <span key={item} style={styles.chip}>
              {item}
              <span style={styles.chipRemove} onClick={() => removeFilter(dutyFilters, setDutyFilters, item)}>✕</span>
            </span>
          ))}
          {statusFilters.map(item => (
            <span key={item} style={styles.chip}>
              {item}
              <span style={styles.chipRemove} onClick={() => removeFilter(statusFilters, setStatusFilters, item)}>✕</span>
            </span>
          ))}
          {managerFilters.map(item => (
            <span key={item} style={styles.chip}>
              {item}
              <span style={styles.chipRemove} onClick={() => removeFilter(managerFilters, setManagerFilters, item)}>✕</span>
            </span>
          ))}
        </div>
      )}


      <style>{inlineCellCss}</style>

      {/* EXCEL STYLE TABLE */}
      {loading && <div style={{textAlign: 'center', padding: '4rem', color: '#64748b'}}>Loading patient data...</div>}
      
      {!loading && !error && (
      <div style={styles.tableCard}>
        <div style={styles.tableWrapper}>
            <table className="pdtbl" style={styles.table}>
                <thead style={styles.thead}>
                    <tr>
                        <th style={{...styles.th, width: '30px'}}>Sl.No</th>
                        <th style={{...styles.th, width: '58px'}}>Duty<br/>Type</th>
                        <th style={{...styles.th, width: '72px'}}>Photo</th>
                        <th style={{...styles.th, width: '72px'}}>Case<br/>Start</th>
                        <th style={{...styles.th, width: '108px'}}>Client Name</th>
                        <th style={{...styles.th, width: '128px'}}>Mobile</th>
                        <th style={{...styles.th, width: '115px'}}>Patient<br/>Name</th>
                        <th style={{...styles.th, width: '48px'}}>Age /<br/>Sex</th>
                        <th style={{...styles.th, width: '145px'}}>Address</th>
                        <th style={{...styles.th, width: '175px'}}>Doctor<br/>Name</th>
                        <th style={{...styles.th, width: '175px'}}>Hospital<br/>Name</th>
                        <th style={{...styles.th, width: '72px'}}>Case Manager</th>
                        <th style={{...styles.th, width: '72px'}}>Status</th>
                        <th style={{...styles.th, width: '44px'}}>View</th>
                        <th style={{...styles.th, width: '44px'}}>Edit</th>
                        <th style={{...styles.th, width: '50px'}}>Delete</th>
                    </tr>
                </thead>
                <tbody style={styles.tbody}>
                    {filteredPatients.length === 0 ? (
                         <tr>
                             <td colSpan={16} style={{padding: '2rem', textAlign: 'center', color: '#64748b'}}>
                                 No patients found matching filters.
                             </td>
                         </tr>
                    ) : (
                        filteredPatients.map((patient, index) => {
                            // 1. Get the list of clients (checking both new and legacy fields)
                            let clients = patient.patient_clients && patient.patient_clients.length > 0 
                                ? patient.patient_clients 
                                : (patient.clients_info || []);
                        clients = [...clients].sort((a: any, b: any) => (a.id || 0) - (b.id || 0));        
                            return (
                            <tr 
                                key={patient.id} 
                                style={{...styles.tr, backgroundColor: hoverRowId === patient.id ? '#f8fafc' : 'white'}}
                                onMouseEnter={() => setHoverRowId(patient.id)}
                                onMouseLeave={() => setHoverRowId(null)}
                            >
                                {/* 1. ID */}
                                <td style={{...styles.td, textAlign: 'center', backgroundColor: '#f8fafc', fontWeight: 600, color: '#94a3b8'}}>{index + 1}</td>
                                
                                {/* 4. Duty Type — editable in place. The menu is
                                    portaled by DutyTypeSelect, so it isn't
                                    clipped by the table's scroll wrapper. */}
                                <td style={{...styles.td, textAlign: 'center',
                                            opacity: savingCell === `${patient.id}:duty_type` ? 0.5 : 1,
                                            pointerEvents: savingCell === `${patient.id}:duty_type` ? 'none' : 'auto'}}>
                                    <DutyTypeSelect
                                        value={getDisplayDuty(patient) === '-' ? null : getDisplayDuty(patient)}
                                        options={dutyOptionsFor(patient)}
                                        onChange={(next) => {
                                            const from = getDisplayDuty(patient);
                                            if (next === from) return;
                                            setPendingEdit({ patient, field: 'duty_type', value: next, title: 'Duty type', from });
                                        }}
                                        title="Change duty type for this case"
                                    />
                                </td>

                                {/* 2. Photo */}
                                <td style={{...styles.td, textAlign: 'center'}}>
                                <ZoomableImage 
                                    src={getImageUrl(patient.photo_url)} 
                                    alt={`${patient.patient_name}'s photo`} 
                                    style={styles.avatar} 
                                    fallbackSrc={PLACEHOLDER_IMG} 
                                />
                                </td>

                                {/* 3. Case Start */}
                                <td style={styles.td}><span style={{whiteSpace: 'nowrap'}}>{formatDate(patient.case_start_date)}</span></td>

                                {/* 4. Client Name */}
                                <td style={{...styles.td, textAlign: 'left', wordBreak: 'break-word'}}>
                                    {clients.map((client, i) => (
                                        <div key={i} style={{...styles.clientBlock, fontWeight: 700, color: '#0f172a'}}>
                                            <CopyableText text={client.client_name || client.name}>
                                                <span>{client.client_name || client.name}</span>
                                            </CopyableText>
                                        </div>
                                    ))}
                                </td>

                                {/* 5. Mobile */}
                                <td style={{...styles.td, textAlign: 'center'}}>
                                    {clients.map((client, i) => (
                                        <div key={i} style={{...styles.contactBlock, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                            <div style={{marginBottom: '2px'}}>
                                                <CopyableText text={client.contact_number_1 || client.contact_number}>
                                                    {client.contact_number_1 || client.contact_number}
                                                </CopyableText>
                                            </div>
                                            {(client.contact_number_2 || client.secondary_contact_number) && (
                                                <div style={{marginTop: '2px'}}>
                                                    <CopyableText text={client.contact_number_2 || client.secondary_contact_number}>
                                                        {client.contact_number_2 || client.secondary_contact_number}
                                                    </CopyableText>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </td>

                                {/* 6. Patient Name */}
                                <td style={{...styles.td, textAlign: 'left'}}>
                                    <div style={styles.boldText}>{patient.patient_name}</div>
                                </td>

                                {/* 7. Age / Sex */}
                                <td style={{...styles.td, whiteSpace: 'nowrap'}}>
                                    {patient.age || '—'} / {patient.sex ? patient.sex.charAt(0).toUpperCase() : '—'}
                                </td>

                                {/* 8. Address */}
                                <td style={{...styles.td, maxWidth: '200px', textAlign: 'left'}}>
                                    <div style={{fontSize: '0.8rem', lineHeight: '1.4', color: '#1e293b'}}>
                                        {patient.address}
                                    </div>
                                    {patient.location_link && (
                                        <div style={{marginTop: '6px', display: 'flex', alignItems: 'center', gap: '2px'}}>
                                            <a
                                                href={patient.location_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                    fontSize: '0.75rem', color: '#2563eb', fontWeight: 600, textDecoration: 'none'
                                                }}
                                            >
                                                <MapPin size={14} />
                                                <span>View on Map</span>
                                            </a>
                                            <CopyButton text={patient.location_link} />
                                        </div>
                                    )}
                                </td>

                                {/* 9. Doctor Name */}
                                <td style={styles.td}>
                                    {patient.treating_details && patient.treating_details.length > 0
                                        ? patient.treating_details.map((td: any, i: number) => (
                                            <div key={i} style={{ fontSize: '0.78rem', color: '#1e293b', marginBottom: i < patient.treating_details!.length - 1 ? '2px' : 0 }}>
                                                {td.doctor || '—'}
                                            </div>
                                        ))
                                        : '—'}
                                </td>

                                {/* 10. Hospital Name */}
                                <td style={styles.td}>
                                    {patient.treating_details && patient.treating_details.length > 0
                                        ? (() => {
                                            const uniqueHospitals = Array.from(new Set(
                                                patient.treating_details
                                                    .map((td: any) => td.hospital)
                                                    .filter(Boolean)
                                            ));
                                            return uniqueHospitals.length > 0
                                                ? uniqueHospitals.map((h: any, i: number) => (
                                                    <div key={i} style={{ fontSize: '0.78rem', color: '#1e293b', marginBottom: i < uniqueHospitals.length - 1 ? '2px' : 0 }}>
                                                        {h}
                                                    </div>
                                                ))
                                                : '—';
                                        })()
                                        : '—'}
                                </td>

                                {/* 11. Case Manager — editable in place. Type to
                                    filter, or type a new name and add it to the
                                    shared reference list from here. */}
                                <td style={styles.td}>
                                    <SearchableSelect
                                        key={`cm-${patient.id}-${cellResetKey}`}
                                        options={caseManagerOptions.map(n => ({ id: n, name: n }))}
                                        value={patient.case_manager || ''}
                                        onChange={(v) => {
                                            const from = patient.case_manager || '—';
                                            if ((v || '') === (patient.case_manager || '')) return;
                                            setPendingEdit({ patient, field: 'case_manager', value: v || null, title: 'Case manager', from });
                                        }}
                                        onAddNew={async (name) => {
                                            await addReferenceOption('case_manager', name);
                                            // Selecting it is left to SearchableSelect, which calls
                                            // onChange with this id — so adding and assigning are
                                            // one action from the table.
                                            return { id: name, name };
                                        }}
                                        variant="inline"
                                        inputClassName={`icell${savingCell === `${patient.id}:case_manager` ? ' saving' : ''}`}
                                        allowClear
                                        addNewLabel="case manager"
                                        placeholder="—"
                                        title="Case manager — type to search or add"
                                    />
                                </td>

                                {/* 12. Status */}
                                <td style={{...styles.td, textAlign: 'center'}}>
                                    {(() => {
                                        const s = getStatusStyle(patient.status);
                                        const isOpen = openStatusId === patient.id;
                                        return (
                                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                                {/* Pill trigger */}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setOpenStatusId(isOpen ? null : patient.id); }}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                        padding: '3px 8px 3px 7px',
                                                        borderRadius: '999px',
                                                        fontSize: '0.68rem', fontWeight: 700,
                                                        border: `1.5px solid ${s.border}`,
                                                        backgroundColor: s.bg, color: s.color,
                                                        cursor: 'pointer', outline: 'none',
                                                        letterSpacing: '0.02em',
                                                        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                                                        transition: 'box-shadow 0.15s',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: s.dot, flexShrink: 0 }} />
                                                    {patient.status || '—'}
                                                    <svg viewBox="0 0 10 10" width="8" height="8" fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="1,3 5,7 9,3" />
                                                    </svg>
                                                </button>
                                                {/* Floating menu */}
                                                {isOpen && (
                                                    <div onClick={e => e.stopPropagation()} style={{
                                                        position: 'absolute', top: 'calc(100% + 5px)', left: '50%', transform: 'translateX(-50%)',
                                                        backgroundColor: 'white', borderRadius: '10px',
                                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.07)',
                                                        border: '1px solid #e2e8f0',
                                                        padding: '4px', zIndex: 999, minWidth: '110px',
                                                    }}>
                                                        {STATUS_OPTIONS.map(opt => {
                                                            const os = getStatusStyle(opt);
                                                            const isSelected = patient.status === opt;
                                                            return (
                                                                <div
                                                                    key={opt}
                                                                    onClick={() => { handleStatusChange(patient, opt); setOpenStatusId(null); }}
                                                                    style={{
                                                                        display: 'flex', alignItems: 'center', gap: '7px',
                                                                        padding: '6px 10px', borderRadius: '7px',
                                                                        fontSize: '0.72rem', fontWeight: isSelected ? 700 : 500,
                                                                        color: isSelected ? os.color : '#334155',
                                                                        backgroundColor: isSelected ? os.bg : 'transparent',
                                                                        cursor: 'pointer', transition: 'background 0.1s',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                    onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = '#f8fafc'; }}
                                                                    onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                                                                >
                                                                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: os.dot, flexShrink: 0 }} />
                                                                    {opt}
                                                                    {isSelected && (
                                                                        <svg style={{ marginLeft: 'auto' }} viewBox="0 0 12 12" width="10" height="10" fill="none" stroke={os.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                            <polyline points="2,6 5,9 10,3" />
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </td>

                                {/* 13. Actions */}
                                <td style={{...styles.td, textAlign: 'center'}}>
                                  <button style={{...styles.textBtn, ...styles.viewBtn}} onClick={() => handleViewDetails(patient.id)}>View</button>
                                </td>
                                <td style={{...styles.td, textAlign: 'center'}}>
                                  <button style={{...styles.textBtn, ...styles.editBtn}} onClick={() => handleEditDetails(patient.id)}>Edit</button>
                                </td>
                                <td style={{...styles.td, textAlign: 'center'}}>
                                  <button style={{...styles.textBtn, ...styles.deleteBtn}} onClick={() => handleDelete(patient.id)}>Delete</button>
                                </td>
                            </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
      </div>
      )}

      <AddPatientModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSuccess={handleSuccess} />
      <ViewPatientModal isOpen={isDetailModalOpen} onClose={() => setIsDetailModalOpen(false)} patientId={selectedPatientId} />
      <EditPatientModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSuccess={handleEditSuccess} patientId={selectedPatientId} />

      {/* Both fields belong to the case, not to this screen — so the dialog
          names where else the change will land before it is written. */}
      <ConfirmDialog
        open={!!pendingEdit}
        title={`Change ${pendingEdit?.title.toLowerCase() || ''} for ${pendingEdit?.patient.patient_name || 'this client'}?`}
        message={`${pendingEdit?.from || '—'} → ${pendingEdit?.value || '—'}`}
        points={
          pendingEdit?.field === 'duty_type'
            ? [
                'Applies to the whole case — bills, the operations board and the case sheet all read this field.',
                'It does not change shifts already scheduled or bills already raised.',
              ]
            : [
                'Applies to the whole case, wherever the case manager is shown.',
                'Nothing already assigned to the previous manager is reassigned.',
              ]
        }
        confirmLabel="Change"
        onConfirm={commitPendingEdit}
        onCancel={cancelPendingEdit}
      />
    </div>
  );
};

export default PatientDashboard;